/**
 * Phase 3F: Governance Controls Types
 * Feature flags, opt-in management, and audit logging
 */

// Feature flag names
export type Phase3FeatureFlag =
  | 'phase_3_enabled'
  | 'pattern_analysis_enabled'
  | 'revenue_flags_enabled'
  | 'subset_insights_enabled'
  | 'financial_variance_enabled';

export interface FeatureFlag {
  id: string;
  flagName: Phase3FeatureFlag;
  isEnabled: boolean;
  description: string;
  createdAt: string;
  updatedAt: string;
  updatedBy?: string;
}

// Client (Hospital) opt-in
export interface ClientOptIn {
  id: string;
  clientId: string;
  optedIn: boolean;
  optedInAt?: string;
  optedInBy?: string;
  optedOutAt?: string;
  optedOutBy?: string;
  acknowledgmentText?: string;
  createdAt: string;
  updatedAt: string;
}

// Surgeon opt-in
export interface SurgeonOptIn {
  id: string;
  surgeonId: string;
  clientId?: string;
  optedIn: boolean;
  optedInAt?: string;
  acknowledgmentText?: string;
  disclaimerAccepted: boolean;
  disclaimerAcceptedAt?: string;
  createdAt: string;
  updatedAt: string;
}

// Audit event types
export type Phase3AuditEventType =
  | 'insight_shown'
  | 'insight_dismissed'
  | 'insight_override'
  | 'feature_flag_changed'
  | 'client_opted_in'
  | 'client_opted_out'
  | 'surgeon_opted_in'
  | 'surgeon_opted_out'
  | 'disclaimer_accepted'
  | 'report_generated'
  | 'report_exported';

export interface Phase3AuditEvent {
  id: string;
  eventType: Phase3AuditEventType;
  actorId: string;
  actorRole?: string;
  clientId?: string;
  surgeonId?: string;
  insightType?: string;
  insightId?: string;
  featureName?: string;
  previousValue?: Record<string, unknown>;
  newValue?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  ipAddress?: string;
  userAgent?: string;
  createdAt: string;
}

// Governance status for a user
export interface GovernanceStatus {
  masterEnabled: boolean;
  clientOptedIn: boolean;
  surgeonOptedIn: boolean;
  disclaimerAccepted: boolean;
  effectiveAccess: boolean;
  featureFlags: Record<Phase3FeatureFlag, boolean>;
}

// Standard disclaimer text
export const PHASE3_DISCLAIMER = 
  "Insights are retrospective observations only. Clinical decisions remain solely with the surgeon. " +
  "LINK does not generate clinical content, suggest procedures, or submit claims.";

export const PHASE3_ACKNOWLEDGMENT_TEXT = 
  "I understand that Phase 3 analytics provide retrospective observations only. " +
  "These insights are advisory in nature and do not constitute clinical recommendations. " +
  "All clinical decisions remain solely with the treating physician. " +
  "LINK does not generate clinical content, suggest additional procedures, or submit claims directly to payers.";

// Opt-in form data
export interface OptInFormData {
  acknowledgmentAccepted: boolean;
  disclaimerAccepted: boolean;
  acknowledgmentText: string;
}
